import { NoyauCard } from '@/components/admin/reglages/noyau-card';
import { NOYAU_TEXTS_IDS } from '@/components/admin/reglages/noyau-texts-contracts';
import type { NoyauTextsDraft } from '@/lib/noyau-texts-draft';
import { NOYAU_TEXT_TOTAL, totalFilledOf, withText } from '@/lib/noyau-texts-draft';
import { totalCountLabel, totalCountSentence } from '@/lib/noyau-texts-labels';
import { cn } from '@/lib/utils';
import { NOYAUX } from '@matter_extractor/shared';
import { Alert } from '@pms-digital/design-system';

/**
 * The eighteen texts of Réglages > Noyaux, GROUPED BY NOYAU (noyau.md §6.3):
 * six cards in the order of the table of §2.1, each with its three fields
 * (maquettes `ReglagesNoyauxParNoyau` and `ReglagesNoyauxVide`).
 *
 * It is the `renderFields` of the layer's editor and nothing more - the head,
 * the history, the autosave and the boxes are `VersionedEditor`'s - so it takes
 * the draft and hands back the next one, one text at a time.
 *
 * ## The state without any version (§6.4)
 *
 * The same grid, with the global counter « n sur 18 remplis » and the callout
 * that says what an absent version costs: an experience carrying a Noyau block
 * does not start. The texts typed meanwhile live in the page only (N8), which
 * the head of the layer says under its title.
 *
 * ## After the v1
 *
 * The note of the maquette under the grid, and the global counter only while a
 * text is missing: a version is always playable (N7), so an emptied text is
 * what greys « Enregistrer une nouvelle version », and the layer's hint - which
 * only knows « rien n'a changé » - would not say so.
 */

export interface NoyauTextsFieldsProps {
  readonly draft: NoyauTextsDraft;
  /** A version opened by « Voir »: nothing is typable. */
  readonly readOnly: boolean;
  /** No version exists yet (§6.4). */
  readonly empty: boolean;
  readonly onDraft: (next: NoyauTextsDraft) => void;
}

export function NoyauTextsFields({ draft, readOnly, empty, onDraft }: NoyauTextsFieldsProps) {
  const total = totalFilledOf(draft);
  const complete = total === NOYAU_TEXT_TOTAL;

  return (
    <div className="space-y-5">
      {empty || !complete ? (
        <div className="flex flex-wrap items-center gap-3">
          <span
            data-testid={NOYAU_TEXTS_IDS.total}
            className={cn(
              'inline-flex items-center whitespace-nowrap rounded-full px-3.5 py-1 font-bold text-small leading-5',
              complete ? 'bg-vert-tint text-vert-deep' : 'bg-gray-100 text-gray-700',
            )}
          >
            {totalCountLabel(total)}
          </span>
          <p className="text-gray-600 text-small">{totalCountSentence(empty)}</p>
        </div>
      ) : null}

      {empty ? (
        <div data-testid={NOYAU_TEXTS_IDS.empty}>
          <Alert tone="info" title="Aucune version encore.">
            Saisissez les douze textes et les six portraits que le spécialiste fournit :
            l’enregistrement s’active quand les dix-huit sont remplis. Tant qu’aucune version
            n’existe, une expérience qui porte un bloc Noyau ne peut pas démarrer.
          </Alert>
        </div>
      ) : null}

      <div className="space-y-1">
        <h3 className="font-semibold text-body text-gray-900">Vue par noyau</h3>
        <p className="text-gray-600 text-small">
          Les trois textes d’un même noyau regroupés dans une carte : premier texte, second texte,
          portrait.
        </p>
      </div>

      <div
        data-testid={NOYAU_TEXTS_IDS.grid}
        className="grid items-start gap-6 md:grid-cols-2 xl:grid-cols-3"
      >
        {NOYAUX.map((noyau) => (
          <NoyauCard
            key={noyau.code}
            noyau={noyau}
            entry={draft.noyaux[noyau.code]}
            readOnly={readOnly}
            onText={(field, text) => onDraft(withText(draft, noyau.code, field, text))}
          />
        ))}
      </div>

      {empty ? null : (
        <div data-testid={NOYAU_TEXTS_IDS.note}>
          <Alert tone="info" title="Un seul objet pour les six noyaux.">
            Le premier enregistrement crée la version 1. Une session déjà commencée garde la version
            qu’elle a chargée à son démarrage : une retouche ici ne l’atteint jamais.
          </Alert>
        </div>
      )}
    </div>
  );
}
